import type { CachedSession } from './cached-session';

const SECURE_SESSION_KEY = 'auth.cachedSession.v1';

type SecureStoragePlugin = {
  set?: (options: { key: string; value: string }) => Promise<void>;
  get?: (options: { key: string }) => Promise<{ value?: string | null }>;
  remove?: (options: { key: string }) => Promise<void>;
  setItem?: (key: string, value: string) => Promise<void>;
  getItem?: (key: string) => Promise<string | null | undefined>;
  removeItem?: (key: string) => Promise<void>;
};

type CapacitorRuntime = {
  registerPlugin?: <T>(name: string) => T;
  isNativePlatform?: () => boolean;
};

function getSecureStoragePlugin(): SecureStoragePlugin | null {
  const capacitor = (globalThis as { Capacitor?: CapacitorRuntime }).Capacitor;
  if (!capacitor?.registerPlugin) {
    return null;
  }

  if (capacitor.isNativePlatform && !capacitor.isNativePlatform()) {
    return null;
  }

  try {
    // Register explicitly so plugin access does not depend on Capacitor.Plugins side effects.
    return capacitor.registerPlugin<SecureStoragePlugin>('SecureStoragePlugin');
  } catch {
    return null;
  }
}

/* istanbul ignore next -- depends on native plugin wiring */
export async function writeCachedSessionToSecureStorage(session: CachedSession): Promise<void> {
  const plugin = getSecureStoragePlugin();
  if (!plugin) {
    return;
  }

  const serialized = JSON.stringify(session);
  if (plugin.set) {
    await plugin.set({ key: SECURE_SESSION_KEY, value: serialized });
    return;
  }

  if (plugin.setItem) {
    await plugin.setItem(SECURE_SESSION_KEY, serialized);
  }
}

/* istanbul ignore next -- depends on native plugin wiring */
export async function readCachedSessionFromSecureStorage(): Promise<CachedSession | null> {
  const plugin = getSecureStoragePlugin();
  if (!plugin) {
    return null;
  }

  let rawValue: string | null | undefined;

  if (plugin.get) {
    const result = await plugin.get({ key: SECURE_SESSION_KEY });
    rawValue = result?.value;
  } else if (plugin.getItem) {
    rawValue = await plugin.getItem(SECURE_SESSION_KEY);
  }

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as CachedSession;
  } catch {
    return null;
  }
}

/* istanbul ignore next -- depends on native plugin wiring */
export async function clearCachedSessionFromSecureStorage(): Promise<void> {
  const plugin = getSecureStoragePlugin();
  if (!plugin) {
    return;
  }

  if (plugin.remove) {
    await plugin.remove({ key: SECURE_SESSION_KEY });
    return;
  }

  if (plugin.removeItem) {
    await plugin.removeItem(SECURE_SESSION_KEY);
  }
}
